import { useState } from "react";
import { Link } from "react-router-dom";
import { Phone, Mail, ArrowRight } from "lucide-react";
import { toast } from "sonner";
import Logo from "@/components/common/Logo";
import SocialLinks from "@/components/common/SocialLinks";
import PaymentBadges from "@/components/common/PaymentBadges";
import { TrustedMarquee } from "@/components/layout/TrustedMarquee";
import { openCookieSettings } from "@/lib/consent";
import api from "@/lib/api";

const FOOTER_NAV = [
  { label: "Home", to: "/" },
  { label: "Shop", to: "/shop" },
  { label: "About", to: "/about" },
  { label: "Reviews", to: "/reviews" },
  { label: "Guides", to: "/guides" },
  { label: "Contact", to: "/contact" },
  { label: "FAQ", to: "/faq" },
];
const LEGAL = [
  { label: "Privacy Policy", to: "/privacy-policy" },
  { label: "Cookie Policy", to: "/cookie-policy" },
  { label: "Terms & Conditions", to: "/terms-and-conditions" },
  { label: "Delivery & Return", to: "/delivery-return-policy" },
];

export const Footer = () => {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);

  const subscribe = async (e) => {
    e.preventDefault();
    if (!email.trim()) return;
    setLoading(true);
    try {
      const res = await api.subscribeNewsletter({ email: email.trim() });
      toast.success(res.message || "Subscribed successfully");
      setEmail("");
    } catch (err) {
      toast.error("Subscription failed. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <footer className="relative overflow-hidden border-t border-[color:var(--acx-border)] bg-[var(--acx-bg-2)]">
      <TrustedMarquee />
      <div className="acx-container relative z-10 px-4 pb-12 pt-16 sm:px-6 lg:px-10">
        <div className="grid grid-cols-2 gap-10 md:grid-cols-4 lg:grid-cols-6">
          <div className="col-span-2">
            <Logo size="lg" />
            <p className="mt-4 max-w-xs text-[14px] leading-relaxed text-white/55">
              Engineered car care products designed to protect, restore and elevate every drive.
            </p>
            <SocialLinks className="mt-5" />

            <div className="mt-6 flex flex-wrap items-center gap-3">
              <a
                href="https://www.amazon.in"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Available at Amazon"
                data-testid="footer-amazon-link"
                className="inline-flex w-fit overflow-hidden rounded-[10px] ring-1 ring-white/10 transition-transform duration-200 hover:scale-[1.03] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/40"
              >
                <img
                  src="/amazon-available.png"
                  alt="Available at Amazon"
                  className="block h-[51px] w-auto"
                  draggable="false"
                />
              </a>
              <a
                href="https://www.flipkart.com"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Available on Flipkart"
                data-testid="footer-flipkart-link"
                className="inline-flex w-fit overflow-hidden rounded-[10px] ring-1 ring-white/10 transition-transform duration-200 hover:scale-[1.03] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/40"
              >
                <img
                  src="/flipkart-available.png"
                  alt="Available on Flipkart"
                  className="block h-[51px] w-auto"
                  draggable="false"
                />
              </a>
            </div>
          </div>

          <div>
            <h4 className="mb-4 text-[12px] uppercase tracking-[0.2em] text-white/50">Menu</h4>
            <ul className="space-y-3">
              {FOOTER_NAV.map((item) => (
                <li key={item.label}>
                  <Link to={item.to} className="text-[14px] text-white/70 transition-colors hover:text-white">
                    {item.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h4 className="mb-4 text-[12px] uppercase tracking-[0.2em] text-white/50">Legal</h4>
            <ul className="space-y-3">
              {LEGAL.map((item) => (
                <li key={item.label}>
                  <Link to={item.to} className="text-[14px] text-white/70 transition-colors hover:text-white">{item.label}</Link>
                </li>
              ))}
              <li>
                <button onClick={openCookieSettings} data-testid="footer-cookie-settings" className="text-[14px] text-white/70 transition-colors hover:text-white">Cookie settings</button>
              </li>
            </ul>
          </div>

          <div className="col-span-2">
            <h4 className="mb-4 text-[12px] uppercase tracking-[0.2em] text-white/50">Contact</h4>
            <ul className="space-y-3 text-[14px] text-white/70">
              <li>
                <a href="tel:+918796027362" className="inline-flex items-center gap-2 transition-colors hover:text-white">
                  <Phone size={15} /> +91-87960 27362
                </a>
              </li>
              <li>
                <a href="mailto:help@autocarex.in" className="inline-flex items-center gap-2 transition-colors hover:text-white">
                  <Mail size={15} /> help@autocarex.in
                </a>
              </li>
            </ul>
            <h4 className="mb-3 mt-6 text-[12px] uppercase tracking-[0.2em] text-white/50">Newsletter</h4>
            <form onSubmit={subscribe} data-testid="footer-newsletter-form" className="flex items-center gap-2">
              <input
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                data-testid="footer-newsletter-input"
                placeholder="Email Address"
                className="h-11 flex-1 rounded-[12px] border border-[color:var(--acx-border)] bg-white/5 px-4 text-[14px] text-white placeholder:text-white/40 focus:border-white/30 focus:outline-none"
              />
              <button
                type="submit"
                disabled={loading}
                data-testid="footer-newsletter-submit"
                className="inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-[12px] bg-white text-black transition-colors hover:bg-white/90 disabled:opacity-60"
                aria-label="Subscribe"
              >
                <ArrowRight size={18} />
              </button>
            </form>
          </div>
        </div>
      </div>

      {/* Payment confidence cues */}
      <div className="relative z-10 border-t border-[color:var(--acx-border)]">
        <div className="acx-container flex flex-col items-center gap-3 px-4 py-6 sm:flex-row sm:justify-center sm:gap-4">
          <span className="text-[12px] uppercase tracking-[0.18em] text-white/45">100% Secure Payments</span>
          <PaymentBadges showCod />
        </div>
      </div>

      {/* Registered business identity (India compliance & transparency) */}
      <div className="relative z-10 border-t border-[color:var(--acx-border)]">
        <div className="acx-container px-4 py-7 sm:px-6 lg:px-10" data-testid="footer-business-identity">
          <div className="grid grid-cols-1 gap-x-10 gap-y-3 text-[13px] leading-relaxed text-white/55 sm:grid-cols-2 lg:grid-cols-4">
            <div>
              <p className="text-[11px] uppercase tracking-[0.16em] text-white/40">Registered Entity</p>
              <p className="mt-1.5 font-medium text-white/75">JT MAX INDIA ENTERPRISES</p>
            </div>
            <div>
              <p className="text-[11px] uppercase tracking-[0.16em] text-white/40">GSTIN</p>
              <p className="mt-1.5 font-medium text-white/75">09AAPFJ4216J1Z1</p>
            </div>
            <div className="lg:col-span-2">
              <p className="text-[11px] uppercase tracking-[0.16em] text-white/40">Registered Address</p>
              <p className="mt-1.5 text-white/65">F-440, F Block, Sector 63, Noida, Uttar Pradesh 201301, India</p>
            </div>
          </div>
          <div className="mt-5 flex items-center gap-3 border-t border-[color:var(--acx-border)] pt-4" data-testid="footer-made-in-india">
            <img src="/make-in-india.png" alt="Make in India" className="h-9 w-auto opacity-90" draggable="false" />
            <span className="text-[12px] font-medium uppercase tracking-[0.16em] text-white/55">Proudly Made in India</span>
          </div>
          <p className="mt-5 border-t border-[color:var(--acx-border)] pt-4 text-[12.5px] text-white/45">
            <span className="font-medium text-white/65">AutoCareX</span> is a brand owned and operated by{" "}
            <span className="font-medium text-white/65">JT MAX INDIA ENTERPRISES</span>. All products are manufactured and marketed under this entity.
          </p>
        </div>
      </div>

      {/* Oversized silver brand wordmark (full-bleed) */}
      <div className="relative z-10 select-none overflow-hidden px-1">
        <span className="block whitespace-nowrap text-center font-display font-bold leading-[0.8] tracking-[-0.05em] text-[#9CA3AF]/55 text-[19vw]">
          AutoCareX
        </span>
      </div>

      {/* Credit line */}
      <div className="relative z-10 mt-6 flex flex-wrap items-center justify-center gap-x-1.5 gap-y-1 px-4 pb-10 text-center text-[13px] font-medium text-white/50">
        <span>™ 2026&nbsp;&nbsp;-&nbsp;&nbsp;AutoCareX&nbsp;&nbsp;-&nbsp;&nbsp;Made with</span>
        <span className="text-[15px] leading-none text-[#E11B22]">♥</span>
        <span>by</span>
        <img src="/brandup-logo.png" alt="brandUp" className="inline-block h-[14px] w-auto translate-y-[0.5px]" draggable="false" />
        <span>in</span>
        <span className="text-[15px] leading-none">🇮🇳</span>
      </div>
    </footer>
  );
};

export default Footer;
